import { configFieldLabel, CONFIG_CATEGORIES, type AuditSettingsGroupId, AUDIT_SETTINGS_GROUPS, configSectionCategory, auditSettingsGroupForPath, configSectionLabel } from '../lib/definitions/settings';
import { useState, useRef, useEffect, useMemo } from 'react';
import { apiGet } from '../api/client';
import { CONFIG_INLINE_SECTIONS, AUTO_RETIRE_TOOLTIP } from '../lib/settingsInline';
import type { KeyboardEvent } from 'react';
import { RadioConfigSection } from '../components/RadioConfigSection';
import { ChannelNamesManager } from '../components/ChannelNamesManager';
import { MeshRegionsManager } from '../components/RegionManager';
import { ScopeListEditor } from '../components/ScopeListEditor';
import { ConnectionsPanel } from '../components/ConnectionsPanel';
import { MeshMapperSettings } from '../components/MeshMapperSettings';
import { MeshMapperRegionOutlines } from '../components/MeshMapperRegionOutlines';
import { FreqMapperSettings } from '../components/FreqMapperSettings';
import { RetiredNodesList } from '../components/RetiredNodesList';
import { scalarSectionKey, fieldDomId, splitPath } from '../components/settings/schema';
import type { ConfigSchemaResponse } from '../components/settings/schema';
import { ReferenceOnlySection } from '../components/settings/FieldEditor';
import { recordNounSingular } from '../components/settings/records';
import { EditableSection } from '../components/settings/EditableSection';
import { ListSectionEditor } from '../components/settings/ListSectionEditor';
import { DynamicKeyedSectionEditor } from '../components/settings/DynamicKeyedSectionEditor';


// --- Search index (2026-09-21) ---------------------------------------------
// One entry per settable field definition across the schema's three shapes
// (fields / dynamic_keyed_sections / list_sections' item_fields) — 201 as of
// this schema, but this is computed from whatever the live schema actually
// returns, never hardcoded. `sectionKey` is whatever key that field's own
// section is rendered/tracked under on this page (the flat section name,
// the dynamic_keyed_sections name, or the list_section's own path) so a
// match can be traced back to "which category/tab to switch to."
interface SearchIndexEntry {
  sectionKey: string;
  path: string;
  label: string;
  description: string;
}


const SCOPE_EDITOR_FIELDS = new Set(["audit.backbone_umbrella_scope_tokens", "audit.scope_names", "audit.scope_colours"]);


function buildSearchIndex(schema: ConfigSchemaResponse | null | "error"): SearchIndexEntry[] {
  if (!schema || schema === "error") return [];
  const idx: SearchIndexEntry[] = [];
  for (const f of schema.fields) {
    if (SCOPE_EDITOR_FIELDS.has(f.path)) continue;
    idx.push({ sectionKey: scalarSectionKey(f.path), path: f.path, label: configFieldLabel(f.path), description: f.description ?? "" });
  }
  idx.push({ sectionKey: "audit", path: "audit.scopes", label: "Scopes", description: "Known scope names, observations, and Regional or Local classification." });
  if (schema.dynamic_keyed_sections) {
    for (const [name, section] of Object.entries(schema.dynamic_keyed_sections)) {
      for (const f of section.item_fields ?? []) {
        idx.push({ sectionKey: name, path: f.path, label: f.path === "label" && !f.choices?.length ? "Name" : configFieldLabel(f.path), description: f.description ?? "" });
      }
    }
  }
  if (schema.list_sections) {
    for (const s of schema.list_sections) {
      for (const f of s.item_fields ?? []) {
        idx.push({ sectionKey: s.path, path: f.path, label: configFieldLabel(f.path), description: f.description ?? "" });
      }
    }
  }
  return idx;
}


// One entry per section this page can navigate to: a flat section, the
// transports dynamic-keyed section, or a list section. `kind` decides which
// pane component renders it.
interface SectionEntry {
  key: string;
  kind: "flat" | "dynamic" | "list" | "mesh_regions_manager" | "channel_names_manager" | "meshmapper_panel" | "freqmapper_panel";
}


// The region manager (components/RegionManager.tsx) is not a schema section:
// it takes the place of the old "MeshMapper regions" tab, whose refresh
// settings now sit in a block under the manager's list (the codes list is
// managed by the manager's add/remove).
const MESH_REGIONS_MANAGER_KEY = "mesh_regions_manager";

const MESH_REGIONS_SECTION = "mesh_regions";

// Channel names we can read with (components/ChannelNamesManager.tsx): not a schema
// section; it sits as a tab right after the MeshCore section.
const CHANNEL_NAMES_MANAGER_KEY = "channel_names_manager";

// MeshMapper coverage on the Live Map (components/MeshMapperSettings.tsx): not a schema form. It holds the API key (never
// in the schema), the switch, the refresh time and a status block.
const MESHMAPPER_SECTION = "meshmapper";
// FreqMapper coverage (components/FreqMapperSettings.tsx): the same shape, a tab beside MeshMapper.
const FREQMAPPER_SECTION = "freqmapper";

// The outline switch, refresh time and countries have their own block (components/MeshMapperRegionOutlines.tsx); the
// generic form below it keeps the rest of the section ("Top-level region scope").
const MESH_REGIONS_HIDDEN_FIELDS = new Set(["mesh_regions.outlines_enabled", "mesh_regions.outlines_refresh_hours", "mesh_regions.outlines_countries"]);


export function SettingsPage({ presetCategory, presetGroup }: { presetCategory?: string | null; presetGroup?: string | null } = {}) {
  const [schema, setSchema] = useState<ConfigSchemaResponse | null | "error">(null);
  // 2026-09-27 first-installer review (part5d, item 6): #/settings?category=<id>
  // (router.ts) lets the no-connection-configured hint elsewhere link straight
  // to a category — e.g. "connections" — instead of always landing on the
  // first tab. An unrecognized/missing id falls back to the original default.
  const [selectedCategory, setSelectedCategory] = useState<string>(
    presetCategory && CONFIG_CATEGORIES.some((c) => c.id === presetCategory) ? presetCategory : CONFIG_CATEGORIES[0].id,
  );
  const [selectedTab, setSelectedTab] = useState<string | null>(null);
  const [selectedAuditGroup, setSelectedAuditGroup] = useState<AuditSettingsGroupId>(
    AUDIT_SETTINGS_GROUPS.some((g) => g.id === presetGroup) ? (presetGroup as AuditSettingsGroupId) : "adverts",
  );
  const [regionsReload, setRegionsReload] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [resultsOpen, setResultsOpen] = useState(false);
  const [searchHighlightPath, setSearchHighlightPath] = useState<string | null>(null);
  const blurTimer = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiGet<ConfigSchemaResponse>("/api/config/schema")
      .then((s) => !cancelled && setSchema(s))
      .catch(() => !cancelled && setSchema("error"));
    return () => {
      cancelled = true;
    };
  }, []);

  const allSectionEntries = useMemo(() => {
    if (!schema || schema === "error") return [] as SectionEntry[];
    const entries: SectionEntry[] = [];
    const seenFlat = new Set<string>();
    for (const f of schema.fields) {
      const section = scalarSectionKey(f.path);
      if (!seenFlat.has(section)) {
        seenFlat.add(section);
        entries.push({ key: section, kind: "flat" });
      }
    }
    if (schema.dynamic_keyed_sections) {
      for (const name of Object.keys(schema.dynamic_keyed_sections)) entries.push({ key: name, kind: "dynamic" });
    }
    if (schema.list_sections) {
      for (const s of schema.list_sections) entries.push({ key: s.path, kind: "list" });
    }
    return entries;
  }, [schema]);

  const sectionsByCategory = useMemo(() => {
    const m = new Map<string, SectionEntry[]>();
    for (const entry of allSectionEntries) {
      if (entry.kind === "flat" && entry.key in CONFIG_INLINE_SECTIONS) continue;
      const cat = configSectionCategory(entry.key);
      if (!m.has(cat)) m.set(cat, []);
      m.get(cat)!.push(
        entry.key === MESH_REGIONS_SECTION ? { key: MESH_REGIONS_MANAGER_KEY, kind: "mesh_regions_manager" }
          : entry.key === MESHMAPPER_SECTION ? { key: MESHMAPPER_SECTION, kind: "meshmapper_panel" }
          : entry.key === FREQMAPPER_SECTION ? { key: FREQMAPPER_SECTION, kind: "freqmapper_panel" } : entry,
      );
      if (entry.key === "meshcore") m.get(cat)!.push({ key: CHANNEL_NAMES_MANAGER_KEY, kind: "channel_names_manager" });
    }
    // Connections opens on the radios tab: the transports section always comes first.
    for (const [cat, list] of m) {
      m.set(cat, [...list.filter((e) => e.key === "transports"), ...list.filter((e) => e.key !== "transports")]);
    }
    return m;
  }, [allSectionEntries]);

  useEffect(() => {
    if (presetCategory && CONFIG_CATEGORIES.some((c) => c.id === presetCategory)) {
      setSelectedCategory(presetCategory);
      setSelectedTab(null);
    }
    if (AUDIT_SETTINGS_GROUPS.some((g) => g.id === presetGroup)) setSelectedAuditGroup(presetGroup as AuditSettingsGroupId);
  }, [presetCategory, presetGroup]);

  // A category only shows in the menu if it actually has at least one
  // section (or is "node", which is never schema-driven and always has its
  // one fixed panel) — an empty category would just be a dead button, not
  // a hidden section: nothing routed through configSectionCategory() is
  // ever dropped, it always resolves to at least "advanced".
  const visibleCategories = useMemo(
    () => CONFIG_CATEGORIES.filter((c) => c.id === "node" || (sectionsByCategory.get(c.id)?.length ?? 0) > 0),
    [sectionsByCategory],
  );

  const categoryLabelById = useMemo(() => new Map(CONFIG_CATEGORIES.map((c) => [c.id, c.label])), []);

  const currentSections = selectedCategory === "node" ? [] : (sectionsByCategory.get(selectedCategory) ?? []);
  const effectiveTab = selectedTab && currentSections.some((s) => s.key === selectedTab) ? selectedTab : (currentSections[0]?.key ?? null);

  useEffect(() => {
    if (selectedCategory === "node" || currentSections.length === 0) return;
    if (selectedTab && currentSections.some((section) => section.key === selectedTab)) return;
    setSelectedTab(currentSections[0].key);
  }, [selectedCategory, selectedTab, currentSections]);

  function selectCategory(catId: string) {
    setSelectedCategory(catId);
    setSearchHighlightPath(null);
    const sections = catId === "node" ? [] : (sectionsByCategory.get(catId) ?? []);
    setSelectedTab(sections[0]?.key ?? null);
  }

  function selectTab(key: string) {
    setSelectedTab(key);
    setSearchHighlightPath(null);
  }

  const searchIndex = useMemo(() => buildSearchIndex(schema), [schema]);
  const query = searchQuery.trim().toLowerCase();
  const searchActive = query.length > 0;
  const matches = useMemo(() => {
    if (!searchActive) return [];
    return searchIndex.filter(
      (e) => e.path.toLowerCase().includes(query) || e.label.toLowerCase().includes(query) || e.description.toLowerCase().includes(query),
    );
  }, [searchIndex, query, searchActive]);
  const visibleMatches = matches.slice(0, 12);

  function activateResult(entry: SearchIndexEntry) {
    const cat = configSectionCategory(entry.sectionKey);
    setSelectedCategory(cat);
    setSelectedTab(entry.sectionKey === MESH_REGIONS_SECTION ? MESH_REGIONS_MANAGER_KEY : entry.sectionKey);
    setSearchHighlightPath(entry.path);
    if (entry.sectionKey === "audit") setSelectedAuditGroup(auditSettingsGroupForPath(entry.path));
    setResultsOpen(false);
  }

  function onSearchKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && visibleMatches.length > 0) {
      e.preventDefault();
      activateResult(visibleMatches[0]);
    } else if (e.key === "Escape") {
      setResultsOpen(false);
    }
  }

  // Scroll the activated field into view once the target category/tab has
  // had a chance to mount.
  useEffect(() => {
    if (!searchHighlightPath) return;
    const sectionKey = effectiveTab;
    if (!sectionKey) return;
    const id = fieldDomId(sectionKey === MESH_REGIONS_MANAGER_KEY ? MESH_REGIONS_SECTION : sectionKey, searchHighlightPath);
    const t = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchHighlightPath, effectiveTab, selectedAuditGroup]);

  useEffect(() => {
    if (presetCategory !== "audit" || !AUDIT_SETTINGS_GROUPS.some((g) => g.id === presetGroup)) return;
    const t = window.setTimeout(() => {
      document.getElementById(`audit-settings-group-${presetGroup}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => window.clearTimeout(t);
  }, [presetCategory, presetGroup, effectiveTab]);

  function onSearchBlur() {
    // A short delay so a click on a result row (which blurs the input
    // first) still registers before the dropdown unmounts.
    blurTimer.current = window.setTimeout(() => setResultsOpen(false), 150);
  }

  function onSearchFocus() {
    if (blurTimer.current) window.clearTimeout(blurTimer.current);
    if (searchActive) setResultsOpen(true);
  }

  const activeSectionEntry = currentSections.find((s) => s.key === effectiveTab) ?? null;

  return (
    <div className="settings-app">
      <div className="settings-app-header">
        <h2>Settings</h2>
        <div className="settings-search-wrap">
          <input
            type="text"
            className="settings-search-input"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setResultsOpen(e.target.value.trim().length > 0);
            }}
            onFocus={onSearchFocus}
            onBlur={onSearchBlur}
            onKeyDown={onSearchKeyDown}
            placeholder="Search all settings…"
            aria-label="Search settings"
          />
          {resultsOpen && searchActive && (
            <div className="settings-search-results">
              <div className="settings-search-count">
                {matches.length} of {searchIndex.length} settings match
              </div>
              {visibleMatches.length === 0 && <div className="settings-search-empty">No settings match this search.</div>}
              {visibleMatches.map((m) => (
                <button
                  key={`${m.sectionKey}--${m.path}`}
                  type="button"
                  className="settings-search-result-row"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => activateResult(m)}
                >
                  <span className="settings-search-result-label">{m.label}</span>
                  <span className="settings-search-result-breadcrumb">
                    {categoryLabelById.get(configSectionCategory(m.sectionKey)) ?? configSectionCategory(m.sectionKey)} &gt; {configSectionLabel(m.sectionKey)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {schema === null && <div className="empty-state">Loading…</div>}
      {schema === "error" && <div className="node-detail-note">Couldn't load the config schema.</div>}
      {schema && schema !== "error" && (
        <div className="settings-body">
          <nav className="settings-menu">
            {visibleCategories.map((c) => (
              <button
                key={c.id}
                type="button"
                className="settings-menu-item"
                data-active={selectedCategory === c.id ? "true" : "false"}
                onClick={() => selectCategory(c.id)}
              >
                {c.label}
              </button>
            ))}
          </nav>

          <section className="settings-content">
            {/* The "node" category's only content is RadioConfigSection,
                which already renders its own "Node Configuration" header
                bar (title + collapse chevron) — this pane-level heading
                would just duplicate it, so it's the one category that
                skips it. RadioConfigSection's own markup is out of scope
                for this rebuild and is left untouched. */}
            {selectedCategory !== "node" && <h2>{categoryLabelById.get(selectedCategory) ?? selectedCategory}</h2>}

            {selectedCategory === "node" ? (
              <RadioConfigSection defaultOpen />
            ) : (
              <>
                {currentSections.length >= 2 && (
                  <div className="settings-tabs">
                    {currentSections.map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        className="settings-tab"
                        data-active={effectiveTab === s.key ? "true" : "false"}
                        onClick={() => selectTab(s.key)}
                      >
                        {s.kind === "mesh_regions_manager" ? "Mesh Regions" : s.kind === "channel_names_manager" ? "Channel Names" : configSectionLabel(s.key)}
                      </button>
                    ))}
                  </div>
                )}

                {activeSectionEntry?.kind === "channel_names_manager" && <ChannelNamesManager />}

                {activeSectionEntry?.kind === "meshmapper_panel" && <MeshMapperSettings />}

                {activeSectionEntry?.kind === "freqmapper_panel" && <FreqMapperSettings />}

                {activeSectionEntry?.kind === "mesh_regions_manager" && (
                  <>
                    <MeshRegionsManager key={regionsReload} />
                    <div className="rf-refresh-block">
                      <h3>MeshMapper region outlines</h3>
                      <MeshMapperRegionOutlines onLoaded={() => setRegionsReload((n) => n + 1)} />
                    </div>
                    <div className="rf-refresh-block">
                      <h3>Region scope</h3>
                      <EditableSection
                        name={MESH_REGIONS_SECTION}
                        fields={schema.fields.filter((f) => splitPath(f.path).section === MESH_REGIONS_SECTION && !MESH_REGIONS_HIDDEN_FIELDS.has(f.path))}
                        highlightPath={searchHighlightPath}
                      />
                    </div>
                  </>
                )}

                {activeSectionEntry?.kind === "flat" && activeSectionEntry.key === "audit" && (
                  <div className="audit-settings-groups">
                    <div className="settings-tabs" role="group" aria-label="Audit settings groups">
                      {AUDIT_SETTINGS_GROUPS.map((group) => (
                        <button
                          key={group.id}
                          type="button"
                          aria-pressed={selectedAuditGroup === group.id}
                          className="settings-tab"
                          data-active={selectedAuditGroup === group.id ? "true" : "false"}
                          onClick={() => setSelectedAuditGroup(group.id)}
                        >
                          {group.label}
                        </button>
                      ))}
                    </div>
                    <p className="audit-settings-general-note">
                      Saved settings take effect immediately for future report calculations. Analysis pages keep showing the latest completed snapshot until the next scheduled refresh. When observations are insufficient, results remain unknown rather than guessed.
                    </p>
                    {AUDIT_SETTINGS_GROUPS.map((group) => {
                      const fields = schema.fields.filter((f) => scalarSectionKey(f.path) === "audit" && !SCOPE_EDITOR_FIELDS.has(f.path) && auditSettingsGroupForPath(f.path) === group.id);
                      return (
                        <section
                          key={group.id}
                          id={`audit-settings-group-${group.id}`}
                          aria-label={group.label}
                          hidden={selectedAuditGroup !== group.id}
                          className="audit-settings-panel"
                        >
                          <p className="audit-settings-intro">{group.description}</p>
                          {group.id === "topology" && (
                            <div id={fieldDomId("audit", "audit.scopes")} className={searchHighlightPath === "audit.scopes" ? "settings-field-search-hit" : undefined}>
                              <ScopeListEditor />
                            </div>
                          )}
                          <EditableSection name="audit" fields={fields} highlightPath={searchHighlightPath} />
                        </section>
                      );
                    })}
                  </div>
                )}

                {activeSectionEntry?.kind === "flat" && activeSectionEntry.key !== "audit" && (
                  <>
                    <EditableSection
                      name={activeSectionEntry.key}
                      fields={schema.fields.filter((f) => scalarSectionKey(f.path) === activeSectionEntry.key && !SCOPE_EDITOR_FIELDS.has(f.path))}
                      highlightPath={searchHighlightPath}
                    />
                  </>
                )}

                {activeSectionEntry?.kind === "dynamic" &&
                  (() => {
                    const d = schema.dynamic_keyed_sections?.[activeSectionEntry.key];
                    if (!d) return null;
                    if (d.path === "transports" && d.value && typeof d.value === "object" && !Array.isArray(d.value)) {
                      return (
                        <ConnectionsPanel
                          key={d.path}
                          path={d.path}
                          description={d.description}
                          itemFields={d.item_fields ?? []}
                          initialValue={d.value}
                        />
                      );
                    }
                    if (d.value && typeof d.value === "object" && !Array.isArray(d.value)) {
                      return (
                        <DynamicKeyedSectionEditor
                          key={d.path}
                          path={d.path}
                          description={d.description}
                          itemFields={d.item_fields ?? []}
                          initialValue={d.value}
                          highlightPath={searchHighlightPath}
                        />
                      );
                    }
                    return (
                      <ReferenceOnlySection
                        sectionKey={d.path}
                        note={
                          d.description ??
                          `The schema describes what fields a ${recordNounSingular(activeSectionEntry.key)} entry has, but does not report the configured instances or their current values, so they cannot be edited here yet.`
                        }
                        fields={d.item_fields ?? []}
                        highlightPath={searchHighlightPath}
                      />
                    );
                  })()}

                {activeSectionEntry?.kind === "list" &&
                  (() => {
                    const s = schema.list_sections?.find((ls) => ls.path === activeSectionEntry.key);
                    if (!s) return null;
                    if (Array.isArray(s.value)) {
                      return (
                        <ListSectionEditor
                          key={s.path}
                          path={s.path}
                          description={s.description}
                          itemFields={s.item_fields ?? []}
                          initialItems={s.value}
                          highlightPath={searchHighlightPath}
                        />
                      );
                    }
                    return (
                      <ReferenceOnlySection
                        sectionKey={s.path}
                        note={s.description ?? "Each item in this list has these fields — shown as a reference; this section does not report live instances yet."}
                        fields={s.item_fields ?? []}
                        highlightPath={searchHighlightPath}
                      />
                    );
                  })()}
              </>
            )}

            {selectedCategory === "system" && (
              <>
                <div title={AUTO_RETIRE_TOOLTIP}>
                  <EditableSection
                    name="nodes"
                    fields={schema.fields.filter((f) => splitPath(f.path).section === "nodes")}
                    highlightPath={searchHighlightPath}
                  />
                </div>
                <RetiredNodesList />
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
